import type { Topic } from "@app/core";
import type { StorageAdapter } from "../adapter/adapter.js";
import { clampAck, type SyncDestination } from "./mut.js";

const TOPIC_CONTENT_SET = `name=?,parent_id=?,color=?,icon=?,owner_id=?,updated_at=?,deleted_at=?`;
const TOPIC_UPDATE_SQL = `UPDATE topics SET ${TOPIC_CONTENT_SET},mut=mut+1 WHERE id=?`;
const TOPIC_UPDATE_SERVER_SQL = `UPDATE topics SET ${TOPIC_CONTENT_SET},mut=mut+1,server_acked_mut=mut+1 WHERE id=?`;

/** CRUD over the `topics` table. Returns plain Topic objects. Topics never reach
 *  Google, so they carry only the server ack column. */
export class TopicsRepository {
  constructor(private readonly db: StorageAdapter) {}

  /** `synced: "server"` marks the row as already in step with the sync server
   *  (an inbound apply); a plain insert starts dirty. */
  async insert(t: Topic, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      `INSERT INTO topics (id,name,parent_id,color,icon,owner_id,mut,server_acked_mut,created_at,updated_at,deleted_at)
       VALUES (?,?,?,?,?,?,?,?,?,?,?)`,
      [t.id, t.name, t.parent_id ?? null, t.color ?? null, t.icon ?? null, t.owner_id ?? null,
       1, synced === "server" ? 1 : 0,
       t.created_at, t.updated_at, t.deleted_at],
    );
  }

  /** Every content write bumps `mut` (the sync engine's per-row dirty fact);
   *  `synced: "server"` self-acknowledges an inbound sync apply. */
  async update(t: Topic, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      synced === "server" ? TOPIC_UPDATE_SERVER_SQL : TOPIC_UPDATE_SQL,
      [t.name, t.parent_id ?? null, t.color ?? null, t.icon ?? null, t.owner_id ?? null,
       t.updated_at, t.deleted_at, t.id],
    );
  }

  /** Clamped acknowledgement — see `TasksRepository.ackMut`. */
  async ackMut(id: string, dest: SyncDestination, upTo: number): Promise<void> {
    if (dest !== "server") return;
    const current = await this.findById(id);
    if (!current) return;
    const next = clampAck(current, dest, upTo);
    if (next === null) return;
    await this.db.execute(`UPDATE topics SET server_acked_mut=? WHERE id=?`, [next, id]);
  }

  async listActive(): Promise<Topic[]> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM topics WHERE deleted_at IS NULL`,
    );
    return rows.map(fromRow);
  }

  /** Every row INCLUDING soft-deleted tombstones — for sync. */
  async listAll(): Promise<Topic[]> {
    const rows = await this.db.select<Record<string, unknown>>(`SELECT * FROM topics`);
    return rows.map(fromRow);
  }

  async findById(id: string): Promise<Topic | null> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM topics WHERE id=?`, [id],
    );
    return rows[0] ? fromRow(rows[0]) : null;
  }

  async hardDelete(id: string): Promise<void> {
    await this.db.execute(`DELETE FROM topics WHERE id=?`, [id]);
  }
}

function fromRow(r: Record<string, unknown>): Topic {
  return {
    id: String(r["id"]),
    name: String(r["name"] ?? ""),
    parent_id: (r["parent_id"] as string | null) ?? null,
    color: (r["color"] as string | null) ?? null,
    icon: (r["icon"] as string | null) ?? null,
    owner_id: (r["owner_id"] as string | null) ?? null,
    mut: Number(r["mut"] ?? 0),
    server_acked_mut: Number(r["server_acked_mut"] ?? 0),
    created_at: String(r["created_at"]),
    updated_at: String(r["updated_at"]),
    deleted_at: (r["deleted_at"] as string | null) ?? null,
  };
}
